import { CheckBox, InputText } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { useErrorOnBlur } from '~/features/reservationInput/hooks/useErrorOnBlur'
import { css, sva } from '~/styled-system/css'
import { placeholder } from '../../lib/placeholder'
import { useIsReserverDifferentFromVisitor } from '../../store/stateMachine/context'
import { useIsMember } from '../../store/stateMachine/state'
import {
  useSetIsReserverDifferentFromVisitor,
  useSetVisitorNameKana,
  useSetVisitorPhoneNumber,
  useVisitorNameKanaError,
  useVisitorPhoneNumberError,
} from '../../store/userInput'
import { NameKana } from '../userInput/NameKana'
import { PhoneNumber } from '../userInput/PhoneNumber'

const styles = sva({
  slots: ['container', 'content', 'title'],
  base: {
    container: {
      spaceY: '3',
    },
    content: {
      spaceY: '2',
      borderTop: 'light',
      paddingTop: '3',
    },
    title: {
      fontSize: 'md',
      fontWeight: 'bold',
    },
  },
})

export function VisitorsInput() {
  const { isMember, isReserverDifferentFromVisitor, onChange } = useVisitorsInput()
  const classes = styles()
  if (!isMember) {
    return null
  }
  return (
    <div className={classes.container}>
      <CheckBox
        label="来店者と予約者が異なる"
        defaultChecked={isReserverDifferentFromVisitor}
        onChange={onChange}
        className={css({ alignItems: 'center' })}
      />
      {isReserverDifferentFromVisitor && (
        <div className={classes.content}>
          <h3 className={classes.title}>来店者情報</h3>
          <div className={classes.container}>
            <NameKanaInput />
            <PhoneNumberInput />
          </div>
        </div>
      )}
    </div>
  )
}

function useVisitorsInput() {
  const isMember = useIsMember()
  const isReserverDifferentFromVisitor = useIsReserverDifferentFromVisitor()
  const change = useSetIsReserverDifferentFromVisitor()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      change(e.target.checked)
    },
    [change]
  )
  return { isMember, isReserverDifferentFromVisitor, onChange }
}

function NameKanaInput() {
  const { error, onChange, onBlur } = useNameKanaInput()
  return (
    <NameKana>
      <InputText
        errorMessage={error}
        width={300}
        placeholder={placeholder.nameKana}
        onChange={onChange}
        withSpread={true}
        onBlur={onBlur}
      />
    </NameKana>
  )
}

function useNameKanaInput() {
  const nameKanaError = useVisitorNameKanaError()
  const { error, onBlur } = useErrorOnBlur(nameKanaError)
  const change = useSetVisitorNameKana()
  // 来店者は　member へリクエストを送らないため、debounce しない
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      change(e.target.value)
    },
    [change]
  )
  return { error, onChange, onBlur }
}

function PhoneNumberInput() {
  const { error, onChange, onBlur } = usePhoneNumberInput()
  return (
    <PhoneNumber>
      <InputText
        errorMessage={error}
        width={300}
        placeholder={placeholder.phoneNumber}
        withSpread={true}
        type="tel"
        onChange={onChange}
        onBlur={onBlur}
      />
      <p className={css({ my: '2', fontSize: 'xs', color: 'gray.700' })}>
        ※携帯電話をお持ちではない場合、固定電話番号を入力ください。
      </p>
    </PhoneNumber>
  )
}
function usePhoneNumberInput() {
  const phoneNumberError = useVisitorPhoneNumberError()
  const { error, onBlur } = useErrorOnBlur(phoneNumberError)
  const change = useSetVisitorPhoneNumber()
  // 来店者は　member へリクエストを送らないため、debounce しない
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      change(e.target.value)
    },
    [change]
  )
  return { error, onChange, onBlur }
}
